import { useEffect, useState } from 'react';
import { useOutletContext, useParams, useSearchParams } from 'react-router';
import type {
  InquiryDto,
  OfferDto,
  ProjectCommentAnchorSummaryDto,
  ProjectDto,
  ProjectMilestone,
} from '@paladin-monorepo/common';
import {
  FundingStatus,
  isMilestoneOverdue,
  isMilestoneStartBlocked,
  MilestoneAction,
  ProjectCommentTargetKind,
  ProjectDeliveryMode,
  projectCommentAnchorKey,
} from '@paladin-monorepo/common';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip, Send, Check, RotateCcw, Play } from 'lucide-react';
import { formatDate } from '@/utils/formatDate';
import { useAuth } from '@/lib/auth';
import { projectsApi } from '@/lib/api/projects';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ProjectCommentsDialogTrigger } from '@/components/project/ProjectCommentsDialogTrigger';
import { resolveMilestonePartyRoles } from '@/lib/project-workspace-roles';
import { MilestoneAcceptanceDialog } from '@/components/project/MilestoneAcceptanceDialog';
import { CheckpointReportForm } from '@/components/project/CheckpointReportForm';
import {
  milestoneDependenciesQueryKey,
  projectGovernanceApi,
  scheduleQueryKey,
} from '@/lib/api/project-governance';
import {
  governanceQueryKey,
  registersQueryKey,
  useControlsMutation,
} from '@/components/project-governance/useGovernanceMutation';
import { MilestoneSchedulePanel } from '@/components/project-governance/MilestoneSchedulePanel';
import { Input } from '@/components/ui/input';

const fundingStatusVariant: Record<FundingStatus, BadgeProps['variant']> = {
  UNFUNDED: 'outline',
  PARTIALLY_FUNDED: 'warning',
  FULLY_FUNDED: 'success',
  NOT_APPLICABLE: 'secondary',
};

function MilestoneCard({
  projectId,
  milestone,
  commentSummary,
  autoOpenComments,
  isSupplier,
  isCustomer,
  canStartNext,
  onAction,
  onOpenHandover,
  onOpenChangeRequest,
  isPending,
  showCheckpoint,
  canAddCheckpoint,
  scheduleNode,
  canEditDuration,
}: {
  projectId: string;
  milestone: ProjectMilestone;
  commentSummary?: ProjectCommentAnchorSummaryDto | null;
  autoOpenComments?: boolean;
  isSupplier: boolean;
  isCustomer: boolean;
  canStartNext: boolean;
  onAction: (milestoneId: string, action: string) => void;
  onOpenHandover: (milestoneId: string) => void;
  onOpenChangeRequest: (milestoneId: string) => void;
  isPending: boolean;
  showCheckpoint?: boolean;
  canAddCheckpoint?: boolean;
  scheduleNode?: { earliestFinish: string; isCritical: boolean; totalFloat: number };
  canEditDuration?: boolean;
}) {
  const { t } = useTranslation();

  const borderColors: Record<ProjectMilestone['status'], string> = {
    NotStarted: 'border-gray-300',
    InProgress: 'border-blue-500',
    Handover: 'border-orange-500',
    Completed: 'border-green-500',
    Blocked: 'border-red-500',
  };

  const statusLabels: Record<ProjectMilestone['status'], string> = {
    NotStarted: t('workspace.milestones.status.notStarted'),
    InProgress: t('workspace.milestones.status.inProgress'),
    Handover: t('workspace.milestones.status.handover'),
    Completed: t('workspace.milestones.status.completed'),
    Blocked: t('workspace.milestones.status.blocked'),
  };

  const showSubmitForReview = isSupplier && milestone.status === 'InProgress';
  const showApproveReject = isCustomer && milestone.status === 'Handover';
  const escrowFundingBlocksStart =
    milestone.fundingStatus != null &&
    milestone.fundingStatus !== 'NOT_APPLICABLE' &&
    milestone.fundingStatus !== 'FULLY_FUNDED';
  const showStartMilestone =
    isSupplier && milestone.status === 'NotStarted' && canStartNext && !escrowFundingBlocksStart;

  const overdue = isMilestoneOverdue(milestone, new Date());
  const leftBorderClass = overdue ? 'border-l-red-600' : borderColors[milestone.status];

  return (
    <div
      id={`milestone-anchor-${milestone.id}`}
      className={`relative rounded-lg border-l-4 ${leftBorderClass} bg-card p-4 pr-12 shadow-sm ${overdue ? 'ring-1 ring-red-200 dark:ring-red-900/40' : ''}`}
    >
      <div className="absolute right-2 top-2 z-10">
        <ProjectCommentsDialogTrigger
          projectId={projectId}
          targetKind={ProjectCommentTargetKind.MILESTONE}
          targetId={milestone.id}
          summary={commentSummary}
          autoOpen={autoOpenComments}
        />
      </div>
      <h3 className="font-medium pr-2">{milestone.title}</h3>
      {scheduleNode && (
        <div className="mb-2 flex flex-wrap gap-2 text-xs">
          <Badge variant={scheduleNode.isCritical ? 'destructive' : 'secondary'}>
            {scheduleNode.isCritical
              ? t('workspace.milestones.schedule.critical')
              : t('workspace.milestones.schedule.float', { days: scheduleNode.totalFloat })}
          </Badge>
          <span className="text-muted-foreground">
            {t('workspace.milestones.schedule.earliestFinish', {
              date: formatDate(scheduleNode.earliestFinish),
            })}
          </span>
        </div>
      )}
      {canEditDuration ? (
        <DurationField projectId={projectId} milestone={milestone} />
      ) : (
        milestone.durationWorkingDays != null && (
          <p className="text-xs text-muted-foreground mb-1">
            {t('workspace.milestones.schedule.duration', { days: milestone.durationWorkingDays })}
          </p>
        )
      )}
      <p className="text-xs text-muted-foreground mb-2">
        {t('workspace.milestones.due')}{' '}
        {milestone.dueDate ? formatDate(milestone.dueDate) : t('generic.unspecified')}
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="inline-flex items-center bg-muted text-foreground/80 text-[10px] font-medium px-2 py-0.5 rounded-full">
          {statusLabels[milestone.status]}
        </span>
        {overdue && (
          <Badge variant="destructive" className="text-[10px]">
            {t('workspace.milestones.overdueBadge')}
          </Badge>
        )}
        {milestone.fundingStatus != null && (
          <Badge variant={fundingStatusVariant[milestone.fundingStatus]} className="text-[10px]">
            {t(`workspace.payments.fundingStatus.${milestone.fundingStatus}`)}
          </Badge>
        )}
      </div>
      <div className="text-sm mb-3">
        <strong>{t('workspace.milestones.assigned')}</strong> {milestone.assignee}
      </div>
      {milestone.handoverComment ? (
        <div className="text-sm mb-3 rounded-md border border-border bg-muted/30 p-2">
          <div className="text-xs font-medium text-muted-foreground mb-1">
            {t('workspace.milestones.handoverMessageTitle')}
          </div>
          <p className="whitespace-pre-wrap text-foreground/90">{milestone.handoverComment}</p>
        </div>
      ) : null}
      {milestone.status === 'InProgress' && milestone.changeRequestComment ? (
        <div className="text-sm mb-3 rounded-md border border-border bg-brand-primary/100/5 p-2">
          <div className="text-xs font-medium text-muted-foreground mb-1">
            {t('workspace.milestones.changeRequestMessageTitle')}
          </div>
          <p className="whitespace-pre-wrap text-foreground/90">{milestone.changeRequestComment}</p>
        </div>
      ) : null}
      <div className="flex items-center text-sm text-muted-foreground mb-3">
        <Paperclip className="w-4 h-4 mr-2" />
        {milestone.files.length > 0 ? (
          <span>{milestone.files.map((f) => f.name).join(', ')}</span>
        ) : (
          <span>{t('workspace.milestones.noFiles')}</span>
        )}
      </div>
      <div className="border-t pt-3 mt-3">
        <label className="flex items-center text-xs text-foreground">
          <input type="checkbox" className="h-4 w-4 mr-2" checked={milestone.confirmations.consultant} readOnly />
          {t('workspace.milestones.consultantConfirms')}
        </label>
        <label className="flex items-center text-xs text-foreground mt-2">
          <input type="checkbox" className="h-4 w-4 mr-2" checked={milestone.confirmations.client} readOnly />
          {t('workspace.milestones.clientConfirms')}
        </label>
      </div>

      {(showSubmitForReview || showApproveReject || showStartMilestone || escrowFundingBlocksStart) && (
        <div className="flex flex-wrap gap-2 pt-3 border-t mt-3">
          {escrowFundingBlocksStart && isSupplier && milestone.status === 'NotStarted' && canStartNext && (
            <p className="w-full text-xs text-muted-foreground mb-1" role="status">
              {t('workspace.milestones.escrowFundingRequired')}
            </p>
          )}
          {showStartMilestone && (
            <Button
              size="sm"
              variant="default"
              disabled={isPending}
              onClick={() => onAction(milestone.id, MilestoneAction.MARK_IN_PROGRESS)}
            >
              <Play className="w-3.5 h-3.5 mr-1.5" />
              {t('workspace.milestones.actions.startMilestone')}
            </Button>
          )}
          {showSubmitForReview && (
            <Button
              size="sm"
              variant="default"
              disabled={isPending}
              onClick={() => onOpenHandover(milestone.id)}
            >
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {t('workspace.milestones.actions.submitForReview')}
            </Button>
          )}
          {showApproveReject && (
            <>
              <Button
                size="sm"
                variant="success"
                disabled={isPending}
                onClick={() => onAction(milestone.id, MilestoneAction.MARK_COMPLETED)}
              >
                <Check className="w-3.5 h-3.5 mr-1.5" />
                {t('workspace.milestones.actions.approve')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={isPending}
                onClick={() => onOpenChangeRequest(milestone.id)}
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                {t('workspace.milestones.actions.requestChanges')}
              </Button>
            </>
          )}
        </div>
      )}
      {showCheckpoint && (
        <CheckpointReportForm
          projectId={projectId}
          milestoneId={milestone.id}
          canAdd={!!canAddCheckpoint}
        />
      )}
    </div>
  );
}

function DurationField({ projectId, milestone }: { projectId: string; milestone: ProjectMilestone }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(
    milestone.durationWorkingDays != null ? String(milestone.durationWorkingDays) : '',
  );
  const mutation = useControlsMutation(projectId, (durationWorkingDays: number | null) =>
    projectGovernanceApi.setMilestoneDuration(projectId, milestone.id, { durationWorkingDays }),
  );
  return (
    <div className="mb-2 flex items-center gap-2">
      <label className="text-xs text-muted-foreground" htmlFor={`duration-${milestone.id}`}>
        {t('workspace.milestones.schedule.durationLabel')}
      </label>
      <Input
        id={`duration-${milestone.id}`}
        type="number"
        min={1}
        className="h-8 w-20"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          const next = value.trim() === '' ? null : Number(value);
          if (next !== null && (!Number.isFinite(next) || next < 1)) return;
          if (next === (milestone.durationWorkingDays ?? null)) return;
          mutation.mutate(next);
        }}
        disabled={mutation.isPending}
      />
    </div>
  );
}

const HANDOVER_COMMENT_MAX = 8000;
const CHANGE_REQUEST_COMMENT_MAX = 8000;

export function ProjectMilestonesPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const { project, inquiry, offer } = useOutletContext<{
    project: ProjectDto;
    inquiry?: InquiryDto;
    offer?: OfferDto;
  }>();
  const { user } = useAuth({ middleware: 'auth', tmp: 'ProjectMilestonesPage' });
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: summariesDto } = useQuery({
    queryKey: ['project-comment-summaries', projectId],
    enabled: !!projectId,
    queryFn: () => projectsApi.getCommentSummaries(projectId as string),
  });
  const { data: governance } = useQuery({
    queryKey: governanceQueryKey(project.id),
    queryFn: () => projectGovernanceApi.get(project.id),
    retry: false,
  });
  const { data: registers } = useQuery({
    queryKey: registersQueryKey(project.id),
    queryFn: () => projectGovernanceApi.listRegisters(project.id),
    enabled: !!governance,
    retry: false,
  });
  const { data: schedule } = useQuery({
    queryKey: scheduleQueryKey(project.id),
    queryFn: () => projectGovernanceApi.getSchedule(project.id),
    enabled: !!governance,
    retry: false,
  });
  const { data: dependencies = [] } = useQuery({
    queryKey: milestoneDependenciesQueryKey(project.id),
    queryFn: () => projectGovernanceApi.listMilestoneDependencies(project.id),
    enabled: !!governance,
    retry: false,
  });
  const scheduleByMilestone = new Map(
    (schedule?.milestones ?? []).map((node) => [String(node.id), node]),
  );

  const highlightCommentId = searchParams.get('commentId');
  const highlightTargetId = searchParams.get('targetId');
  const highlightTargetKind = searchParams.get('targetKind');

  useEffect(() => {
    const commentId = searchParams.get('commentId');
    const targetKind = searchParams.get('targetKind');
    const targetId = searchParams.get('targetId');
    if (!commentId || !targetId || targetKind !== ProjectCommentTargetKind.MILESTONE) {
      return;
    }
    const anchor = document.getElementById(`milestone-anchor-${targetId}`);
    anchor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.requestAnimationFrame(() => {
      document.getElementById(`comment-${commentId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [searchParams]);

  const [handoverOpen, setHandoverOpen] = useState(false);
  const [handoverMilestoneId, setHandoverMilestoneId] = useState<string | null>(null);
  const [handoverDraft, setHandoverDraft] = useState('');

  const [changeRequestOpen, setChangeRequestOpen] = useState(false);
  const [changeRequestMilestoneId, setChangeRequestMilestoneId] = useState<string | null>(null);
  const [changeRequestDraft, setChangeRequestDraft] = useState('');

  const [acceptanceMilestoneId, setAcceptanceMilestoneId] = useState<string | null>(null);

  const { isSupplier, isCustomer } = resolveMilestonePartyRoles(
    user?.account?.id,
    inquiry,
    offer,
  );
  const internalDelivery = project.deliveryMode === ProjectDeliveryMode.Internal;

  const milestoneActionMutation = useMutation({
    mutationFn: ({
      milestoneId,
      action,
      handoverComment,
      changeRequestComment,
      evidenceFileIds,
    }: {
      milestoneId: string;
      action: string;
      handoverComment?: string;
      changeRequestComment?: string;
      evidenceFileIds?: string[];
    }) =>
      projectsApi.updateMilestone(project.id, milestoneId, {
        action: action as (typeof MilestoneAction)[keyof typeof MilestoneAction],
        ...(action === MilestoneAction.MARK_HANDOVER ? { handoverComment } : {}),
        ...(action === MilestoneAction.REQUEST_CHANGES ? { changeRequestComment } : {}),
        ...(evidenceFileIds ? { evidenceFileIds } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      setHandoverOpen(false);
      setHandoverMilestoneId(null);
      setHandoverDraft('');
      setChangeRequestOpen(false);
      setChangeRequestMilestoneId(null);
      setChangeRequestDraft('');
      setAcceptanceMilestoneId(null);
    },
  });

  const sortedMilestones = [...project.milestones].sort(
    (a, b) => Number(a.id) - Number(b.id),
  );

  const canStartNextMap = new Map<string, boolean>();
  sortedMilestones.forEach((m) => {
    canStartNextMap.set(
      m.id,
      !isMilestoneStartBlocked(
        Number(m.id),
        dependencies,
        sortedMilestones.map((row) => ({ id: row.id, status: row.status })),
      ),
    );
  });

  const openHandover = (milestoneId: string) => {
    setHandoverMilestoneId(milestoneId);
    setHandoverDraft('');
    setHandoverOpen(true);
  };

  const confirmHandover = () => {
    if (!handoverMilestoneId) return;
    milestoneActionMutation.mutate({
      milestoneId: handoverMilestoneId,
      action: MilestoneAction.MARK_HANDOVER,
      handoverComment: handoverDraft.trim() || undefined,
    });
  };

  const openChangeRequest = (milestoneId: string) => {
    setChangeRequestMilestoneId(milestoneId);
    setChangeRequestDraft('');
    setChangeRequestOpen(true);
  };

  const confirmChangeRequest = () => {
    if (!changeRequestMilestoneId) return;
    const trimmed = changeRequestDraft.trim();
    if (!trimmed.length) return;
    milestoneActionMutation.mutate({
      milestoneId: changeRequestMilestoneId,
      action: MilestoneAction.REQUEST_CHANGES,
      changeRequestComment: trimmed,
    });
  };

  return (
    <>
      {schedule?.projectEnd && (
        <p className="mb-4 text-sm text-muted-foreground">
          {t('workspace.milestones.schedule.projectEnd', { date: formatDate(schedule.projectEnd) })}
        </p>
      )}
      {governance && (
        <MilestoneSchedulePanel
          projectId={project.id}
          milestones={sortedMilestones}
          schedule={schedule}
          dependencies={dependencies}
          governance={governance}
        />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {sortedMilestones.map((m) => (
          <MilestoneCard
            key={m.id}
            projectId={projectId ?? project.id}
            milestone={m}
            commentSummary={
              summariesDto?.summaries[
                projectCommentAnchorKey(ProjectCommentTargetKind.MILESTONE, m.id)
              ] ?? null
            }
            autoOpenComments={
              !!highlightCommentId &&
              highlightTargetKind === ProjectCommentTargetKind.MILESTONE &&
              highlightTargetId === m.id
            }
            isSupplier={internalDelivery ? !!user?.id && m.teamManagerUserId === user.id : isSupplier}
            isCustomer={
              internalDelivery || m.stageId ? !!user?.id && m.acceptanceUserId === user.id : isCustomer
            }
            canStartNext={canStartNextMap.get(m.id) ?? false}
            onAction={(milestoneId, action) =>
              action === MilestoneAction.MARK_COMPLETED && m.stageId
                ? setAcceptanceMilestoneId(milestoneId)
                : milestoneActionMutation.mutate({ milestoneId, action })
            }
            onOpenHandover={openHandover}
            onOpenChangeRequest={openChangeRequest}
            isPending={milestoneActionMutation.isPending}
            showCheckpoint={!!governance}
            canAddCheckpoint={!!registers?.permissions.canAddCheckpoint}
            scheduleNode={scheduleByMilestone.get(m.id)}
            canEditDuration={
              !!governance &&
              governance.state !== 'closed' &&
              (governance.permissions.isProjectManager ||
                (!!user?.id && m.teamManagerUserId === user.id))
            }
          />
        ))}
      </div>

      <MilestoneAcceptanceDialog
        projectId={project.id}
        open={!!acceptanceMilestoneId}
        onOpenChange={(open) => !open && setAcceptanceMilestoneId(null)}
        onConfirm={(evidenceFileIds) =>
          acceptanceMilestoneId &&
          milestoneActionMutation.mutate({
            milestoneId: acceptanceMilestoneId,
            action: MilestoneAction.MARK_COMPLETED,
            evidenceFileIds,
          })
        }
        isPending={milestoneActionMutation.isPending}
      />

      <Dialog
        open={handoverOpen}
        onOpenChange={(open) => {
          setHandoverOpen(open);
          if (!open) {
            setHandoverMilestoneId(null);
            setHandoverDraft('');
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('workspace.milestones.handover.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('workspace.milestones.handover.dialogDescription')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-1">
            <Label htmlFor="handover-comment">{t('workspace.milestones.handover.commentLabel')}</Label>
            <Textarea
              id="handover-comment"
              value={handoverDraft}
              onChange={(e) => setHandoverDraft(e.target.value.slice(0, HANDOVER_COMMENT_MAX))}
              placeholder={t('workspace.milestones.handover.commentPlaceholder')}
              rows={5}
              maxLength={HANDOVER_COMMENT_MAX}
              disabled={milestoneActionMutation.isPending}
            />
            <p className="text-xs text-muted-foreground text-right">
              {handoverDraft.length}/{HANDOVER_COMMENT_MAX}
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setHandoverOpen(false)}
              disabled={milestoneActionMutation.isPending}
            >
              {t('workspace.milestones.handover.cancel')}
            </Button>
            <Button type="button" onClick={confirmHandover} disabled={milestoneActionMutation.isPending}>
              {t('workspace.milestones.handover.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={changeRequestOpen}
        onOpenChange={(open) => {
          setChangeRequestOpen(open);
          if (!open) {
            setChangeRequestMilestoneId(null);
            setChangeRequestDraft('');
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('workspace.milestones.changeRequest.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('workspace.milestones.changeRequest.dialogDescription')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-1">
            <Label htmlFor="change-request-comment">{t('workspace.milestones.changeRequest.commentLabel')}</Label>
            <Textarea
              id="change-request-comment"
              value={changeRequestDraft}
              onChange={(e) => setChangeRequestDraft(e.target.value.slice(0, CHANGE_REQUEST_COMMENT_MAX))}
              placeholder={t('workspace.milestones.changeRequest.commentPlaceholder')}
              rows={5}
              maxLength={CHANGE_REQUEST_COMMENT_MAX}
              disabled={milestoneActionMutation.isPending}
            />
            <p className="text-xs text-muted-foreground text-right">
              {changeRequestDraft.length}/{CHANGE_REQUEST_COMMENT_MAX}
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setChangeRequestOpen(false)}
              disabled={milestoneActionMutation.isPending}
            >
              {t('workspace.milestones.changeRequest.cancel')}
            </Button>
            <Button
              type="button"
              onClick={confirmChangeRequest}
              disabled={!changeRequestDraft.trim() || milestoneActionMutation.isPending}
            >
              {t('workspace.milestones.changeRequest.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

ProjectMilestonesPage.displayName = 'WorkspaceMilestonesPage';
